import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { theme } from '../styles/theme';
import Button from './ui/Button';
import { FaCheck, FaCrown, FaCalendar, FaLink } from 'react-icons/fa';

const SubscriptionManagement: React.FC = () => {
  const { subscription, refreshSubscription } = useAuth();
  const navigate = useNavigate();
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    setError(null);

    try {
      const response = await api.subscription.cancelSubscription();
      if (response.success) {
        await refreshSubscription();
        setShowCancelConfirm(false);
        alert('Your subscription will be cancelled at the end of the current billing period.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  if (!subscription) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        color: theme.colors.text.secondary
      }}>
        Loading subscription...
      </div>
    );
  }

  const isPro = subscription.planTier === 'pro';
  const isActive = subscription.status === 'active';
  const inGracePeriod = subscription.gracePeriodEnd && new Date(subscription.gracePeriodEnd) > new Date();
  const isCancelled = subscription.cancelAtPeriodEnd === true;

  return (
    <div style={{
      padding: '48px 24px',
      maxWidth: '900px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{
          fontSize: theme.typography.sizes['3xl'],
          fontWeight: theme.typography.weights.bold,
          color: theme.colors.text.primary,
          marginBottom: '8px'
        }}>
          Subscription
        </h1>
        <p style={{
          fontSize: theme.typography.sizes.base,
          color: theme.colors.text.secondary,
          margin: 0
        }}>
          Manage your subscription and billing
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '16px',
          backgroundColor: '#FEF2F2',
          border: '1px solid #FCA5A5',
          borderRadius: '8px',
          color: '#DC2626',
          marginBottom: '24px'
        }}>
          {error}
        </div>
      )}

      {/* Grace Period Warning */}
      {inGracePeriod && (
        <div style={{
          padding: '16px',
          backgroundColor: '#FEF3C7',
          border: '1px solid #F59E0B',
          borderRadius: '8px',
          color: '#92400E',
          marginBottom: '24px'
        }}>
          <strong>Grace Period Active:</strong> Your subscription has expired but you have until{' '}
          {new Date(subscription.gracePeriodEnd!).toLocaleDateString()} to renew before access is restricted.
        </div>
      )}

      {/* Cancellation Warning */}
      {isCancelled && subscription.currentPeriodEnd && (
        <div style={{
          padding: '16px',
          backgroundColor: '#FEF3C7',
          border: '1px solid #F59E0B',
          borderRadius: '8px',
          color: '#92400E',
          marginBottom: '24px'
        }}>
          <strong>Subscription Ending:</strong> Your subscription will end on{' '}
          {new Date(subscription.currentPeriodEnd).toLocaleDateString()}. You can reactivate anytime before then by contacting support.
        </div>
      )}

      {/* Current Plan Card */}
      <div style={{
        backgroundColor: theme.colors.surface,
        border: `2px solid ${isPro ? theme.colors.primary : theme.colors.border}`,
        borderRadius: '12px',
        padding: '32px',
        marginBottom: '24px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {isPro && (
              <div style={{ color: theme.colors.primary }}>
                {FaCrown({ size: 24 })}
              </div>
            )}
            <div>
              <h2 style={{
                fontSize: theme.typography.sizes['2xl'],
                fontWeight: theme.typography.weights.bold,
                color: theme.colors.text.primary,
                margin: 0
              }}>
                {subscription.planTier.charAt(0).toUpperCase() + subscription.planTier.slice(1)} Plan
              </h2>
              <p style={{
                fontSize: theme.typography.sizes.sm,
                color: theme.colors.text.secondary,
                margin: '4px 0 0 0'
              }}>
                Status: <strong style={{
                  color: isActive ? '#059669' : '#DC2626'
                }}>
                  {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                </strong>
              </p>
            </div>
          </div>
          {!isPro && (
            <Button
              variant="primary"
              size="md"
              onClick={handleUpgrade}
            >
              Upgrade to Pro
            </Button>
          )}
        </div>

        {/* Usage Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '24px',
          padding: '24px',
          backgroundColor: theme.colors.background,
          borderRadius: '8px'
        }}>
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px'
            }}>
              <div style={{ color: theme.colors.primary }}>
                {FaLink({ size: 16 })}
              </div>
              <span style={{
                fontSize: theme.typography.sizes.sm,
                color: theme.colors.text.secondary,
                fontWeight: theme.typography.weights.semibold
              }}>
                Trackable Links
              </span>
            </div>
            <p style={{
              fontSize: theme.typography.sizes['2xl'],
              fontWeight: theme.typography.weights.bold,
              color: theme.colors.text.primary,
              margin: 0
            }}>
              {subscription.linkLimit === -1
                ? `${subscription.linkCount} / Unlimited`
                : `${subscription.linkCount} / ${subscription.linkLimit}`}
            </p>
          </div>

          {subscription.currentPeriodEnd && (
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <div style={{ color: theme.colors.primary }}>
                  {FaCalendar({ size: 16 })}
                </div>
                <span style={{
                  fontSize: theme.typography.sizes.sm,
                  color: theme.colors.text.secondary,
                  fontWeight: theme.typography.weights.semibold
                }}>
                  Billing Period
                </span>
              </div>
              <p style={{
                fontSize: theme.typography.sizes.base,
                fontWeight: theme.typography.weights.medium,
                color: isCancelled ? '#DC2626' : theme.colors.text.primary,
                margin: 0
              }}>
                {isCancelled ? 'Ends' : 'Renews'} {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Plan Features */}
      <div style={{
        backgroundColor: theme.colors.surface,
        border: `1px solid ${theme.colors.border}`,
        borderRadius: '12px',
        padding: '32px',
        marginBottom: '24px'
      }}>
        <h3 style={{
          fontSize: theme.typography.sizes.xl,
          fontWeight: theme.typography.weights.semibold,
          color: theme.colors.text.primary,
          marginBottom: '16px'
        }}>
          Your Plan Includes
        </h3>
        <ul style={{
          listStyle: 'none',
          padding: 0,
          margin: 0
        }}>
          {isPro ? (
            <>
              <li style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '12px'
              }}>
                <div style={{ color: theme.colors.primary }}>
                  {FaCheck({ size: 16 })}
                </div>
                <span style={{ fontSize: theme.typography.sizes.base, color: theme.colors.text.primary }}>
                  Unlimited trackable links
                </span>
              </li>
              <li style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '12px'
              }}>
                <div style={{ color: theme.colors.primary }}>
                  {FaCheck({ size: 16 })}
                </div>
                <span style={{ fontSize: theme.typography.sizes.base, color: theme.colors.text.primary }}>
                  Advanced analytics
                </span>
              </li>
              <li style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '12px'
              }}>
                <div style={{ color: theme.colors.primary }}>
                  {FaCheck({ size: 16 })}
                </div>
                <span style={{ fontSize: theme.typography.sizes.base, color: theme.colors.text.primary }}>
                  Priority support
                </span>
              </li>
              <li style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '12px'
              }}>
                <div style={{ color: theme.colors.primary }}>
                  {FaCheck({ size: 16 })}
                </div>
                <span style={{ fontSize: theme.typography.sizes.base, color: theme.colors.text.primary }}>
                  Custom branding
                </span>
              </li>
              <li style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{ color: theme.colors.primary }}>
                  {FaCheck({ size: 16 })}
                </div>
                <span style={{ fontSize: theme.typography.sizes.base, color: theme.colors.text.primary }}>
                  API access
                </span>
              </li>
            </>
          ) : (
            <>
              <li style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '12px'
              }}>
                <div style={{ color: theme.colors.primary }}>
                  {FaCheck({ size: 16 })}
                </div>
                <span style={{ fontSize: theme.typography.sizes.base, color: theme.colors.text.primary }}>
                  Up to 25 trackable links
                </span>
              </li>
              <li style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '12px'
              }}>
                <div style={{ color: theme.colors.primary }}>
                  {FaCheck({ size: 16 })}
                </div>
                <span style={{ fontSize: theme.typography.sizes.base, color: theme.colors.text.primary }}>
                  Basic analytics
                </span>
              </li>
              <li style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{ color: theme.colors.primary }}>
                  {FaCheck({ size: 16 })}
                </div>
                <span style={{ fontSize: theme.typography.sizes.base, color: theme.colors.text.primary }}>
                  Email support
                </span>
              </li>
            </>
          )}
        </ul>
      </div>

      {/* Cancel Subscription */}
      {isPro && isActive && !isCancelled && (
        <div style={{
          backgroundColor: theme.colors.surface,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: '12px',
          padding: '32px'
        }}>
          <h3 style={{
            fontSize: theme.typography.sizes.xl,
            fontWeight: theme.typography.weights.semibold,
            color: theme.colors.text.primary,
            marginBottom: '8px'
          }}>
            Cancel Subscription
          </h3>
          <p style={{
            fontSize: theme.typography.sizes.base,
            color: theme.colors.text.secondary,
            marginBottom: '16px'
          }}>
            Your subscription will remain active until the end of your current billing period.
          </p>

          {!showCancelConfirm ? (
            <Button
              variant="secondary"
              size="md"
              onClick={() => setShowCancelConfirm(true)}
              style={{
                borderColor: '#DC2626',
                color: '#DC2626'
              }}
            >
              Cancel Subscription
            </Button>
          ) : (
            <div style={{
              padding: '16px',
              backgroundColor: '#FEF2F2',
              border: '1px solid #FCA5A5',
              borderRadius: '8px'
            }}>
              <p style={{
                fontSize: theme.typography.sizes.base,
                color: '#DC2626',
                marginBottom: '16px',
                fontWeight: theme.typography.weights.semibold
              }}>
                Are you sure you want to cancel? You'll lose access to Pro features at the end of your billing period.
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={cancelling}
                >
                  Keep Subscription
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                  style={{
                    backgroundColor: '#DC2626'
                  }}
                >
                  {cancelling ? 'Cancelling...' : 'Confirm Cancellation'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;
